import { useState, useEffect, useCallback } from "react";
import { Download, Mail, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAdminAuth } from "@/lib/AdminAuthContext";
import { adminFetchJson } from "@/lib/adminFetch";
import { Link } from "react-router-dom";
import AdminLayout from "./AdminLayout";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function NewsletterPanel() {
  useAdminAuth(); // ensures provider exists; auth handled via interceptor
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [items, setItems] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await adminFetchJson("/api/admin/newsletter/subscribers?limit=500");
      setTotal(data.total || 0);
      setItems(data.items || []);
    } catch (e) {
      toast.error(`No se pudieron cargar suscriptores: ${e.message}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const downloadCSV = async () => {
    try {
      const token = localStorage.getItem("admin_token");
      const res = await fetch(`${BACKEND_URL}/api/admin/newsletter/export`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `newsletter-subscribers-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("CSV descargado");
    } catch (e) {
      toast.error(`Falló la descarga: ${e.message}`);
    }
  };

  return (
    <AdminLayout>
      <div className="mb-8 flex items-start justify-between gap-4">
        <div>
          <p className="text-[10px] uppercase tracking-[0.28em] text-[#bc6c25] font-semibold">Newsletter</p>
          <h1 className="font-serif text-3xl md:text-4xl text-[#1a2421] mt-1">Suscriptores</h1>
          <p className="text-sm text-[#5c6b61] mt-2">
            Personas que dejaron su correo desde el pop-up en el blog.
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={downloadCSV}
            variant="outline"
            data-testid="newsletter-export-btn"
            className="border-[#2c4a3b]/20"
          >
            <Download className="w-4 h-4 mr-2" /> Exportar CSV
          </Button>
          <Link to="/admin/broadcast">
            <Button data-testid="goto-broadcast-btn" className="bg-[#bc6c25] hover:bg-[#9c581e] text-white">
              <Mail className="w-4 h-4 mr-2" /> Enviar broadcast
            </Button>
          </Link>
        </div>
      </div>

      <div className="rounded-2xl border border-[#2c4a3b]/10 bg-white overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#2c4a3b]/10">
          <p className="text-sm text-[#1a2421]">
            <span className="font-semibold">{total}</span> suscriptores totales
          </p>
          {loading && <Loader2 className="w-4 h-4 animate-spin text-[#5c6b61]" />}
        </div>
        {items.length === 0 ? (
          <div className="p-10 text-center text-[#5c6b61] text-sm">
            {loading ? "Cargando..." : "Todavía no hay suscriptores. El pop-up se activa al 60% de scroll en cualquier artículo del blog."}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#f0ebe1] text-[10px] uppercase tracking-[0.18em] text-[#5c6b61]">
                <tr>
                  <th className="text-left px-5 py-3">Email</th>
                  <th className="text-left px-5 py-3">Nombre</th>
                  <th className="text-left px-5 py-3">Origen</th>
                  <th className="text-left px-5 py-3">Idioma</th>
                  <th className="text-left px-5 py-3">Visto por primera vez</th>
                </tr>
              </thead>
              <tbody>
                {items.map((s, i) => (
                  <tr key={s.email} className={i % 2 ? "bg-[#f9f6f0]/50" : ""} data-testid={`subscriber-row-${i}`}>
                    <td className="px-5 py-3 font-medium text-[#1a2421]">{s.email}</td>
                    <td className="px-5 py-3 text-[#5c6b61]">{s.name || "—"}</td>
                    <td className="px-5 py-3 text-[#5c6b61]">{s.first_source || "—"}</td>
                    <td className="px-5 py-3 text-[#5c6b61]">{s.language || "es"}</td>
                    <td className="px-5 py-3 text-[#5c6b61]">{(s.first_seen_at || "").slice(0, 10)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
